/**
 * MapOverlay - Displays an interactive map with geographic stage markers
 *
 * Features:
 * - Uses Leaflet with OpenStreetMap tiles (free, no API key needed)
 * - Multiple map styles (streets, satellite, terrain)
 * - Mini-map view (collapsed) and full-screen view (expanded)
 * - Click markers to navigate to stages
 * - Current stage highlighted
 * - Auto-fit to marker bounds
 */

import type {
  NavigationConfig,
  NavigationMarker,
  GeoCoordinates,
  ExperienceStage
} from '../types/config';
import { getTileLayerConfig, DEFAULT_MAP_CENTER } from '../config/mapTileLayers';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('MapOverlay');

// Leaflet types are declared in src/types/leaflet.d.ts

export interface MapOverlayEvents {
  onMarkerClick: (stageId: string) => void;
  onMarkerHover: (stageId: string | null) => void;
  onExpandToggle: (isExpanded: boolean) => void;
  onMinimapClick?: () => void;  // Click on minimap opens stage selector
}

export class MapOverlay {
  private container: HTMLElement;
  private config: NavigationConfig;
  private stages: ExperienceStage[];
  private events: MapOverlayEvents;

  private currentStageId: string | null = null;
  private isExpanded: boolean = false;
  private leafletLoaded: boolean = false;

  private overlayElement: HTMLElement | null = null;
  private minimapContainer: HTMLElement | null = null;
  private fullscreenContainer: HTMLElement | null = null;

  private minimapMap: L.Map | null = null;
  private fullscreenMap: L.Map | null = null;
  private markers: Map<string, { minimap: L.Marker | null; fullscreen: L.Marker | null }> = new Map();

  constructor(
    container: HTMLElement,
    config: NavigationConfig,
    stages: ExperienceStage[],
    events: MapOverlayEvents
  ) {
    this.container = container;
    this.config = config;
    this.stages = stages;
    this.events = events;
  }

  /**
   * Load Leaflet library dynamically
   */
  private async loadLeaflet(): Promise<void> {
    if (this.leafletLoaded || typeof L !== 'undefined') {
      this.leafletLoaded = true;
      return;
    }

    return new Promise((resolve, reject) => {
      // Load CSS
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      cssLink.crossOrigin = '';
      document.head.appendChild(cssLink);

      // Load JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => {
        this.leafletLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Render the map overlay
   */
  public async render(): Promise<void> {
    // Load Leaflet first
    await this.loadLeaflet();

    // Determine if minimap should show - use new showMinimap or legacy displayMode
    // showMinimap is the new simple boolean; displayMode is legacy
    const shouldShowMinimap = this.config.showMinimap !== undefined
      ? this.config.showMinimap
      : (this.config.displayMode !== 'fullscreen' && this.config.displayMode !== 'none');

    // If minimap is disabled, don't render anything (fullscreen is now in stage selector)
    if (!shouldShowMinimap) {
      return;
    }

    // Create main overlay container
    this.overlayElement = document.createElement('div');
    this.overlayElement.className = 'map-overlay';
    this.overlayElement.setAttribute('data-mode', 'minimap');

    // Create mini-map view (100x100px, bottom-left)
    this.createMinimapView();

    // Add to container
    this.container.appendChild(this.overlayElement);

    // Initialize maps after DOM is ready
    requestAnimationFrame(() => {
      this.initializeMaps();
    });
  }

  /**
   * Create the mini-map view
   */
  private createMinimapView(): void {
    if (!this.overlayElement) return;

    // Minimap is now always 100x100px, bottom-left (hardcoded in CSS)
    this.minimapContainer = document.createElement('div');
    this.minimapContainer.className = 'map-minimap';

    this.minimapContainer.innerHTML = `
      <div class="map-container" id="minimap-container"></div>
    `;

    // Click on minimap opens stage selector (will be handled by event)
    this.minimapContainer.addEventListener('click', () => {
      this.events.onMinimapClick?.();
    });

    this.overlayElement.appendChild(this.minimapContainer);
  }

  /**
   * Initialize Leaflet maps
   */
  private initializeMaps(): void {
    if (typeof L === 'undefined') {
      logger.error('Leaflet not loaded');
      return;
    }

    const defaultCenter = this.getDefaultCenter();
    const defaultZoom = this.config.map?.defaultZoom || 4;
    const style = this.config.map?.style || 'streets';
    const tileConfig = getTileLayerConfig(style);

    // Initialize minimap
    const minimapElement = document.getElementById('minimap-container');
    if (minimapElement) {
      this.minimapMap = L.map(minimapElement, {
        center: [defaultCenter.lat, defaultCenter.lng],
        zoom: defaultZoom,
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
      });

      L.tileLayer(tileConfig.url, {
        attribution: tileConfig.attribution
      }).addTo(this.minimapMap);

      this.addMarkersToMap(this.minimapMap, 'minimap');
      // Center minimap on current stage (or first stage) with focused zoom
      this.centerMinimapOnCurrentStage();
    }

    // Initialize fullscreen map
    const fullscreenElement = document.getElementById('fullscreen-map-container');
    if (fullscreenElement) {
      this.fullscreenMap = L.map(fullscreenElement, {
        center: [defaultCenter.lat, defaultCenter.lng],
        zoom: defaultZoom,
      });

      L.tileLayer(tileConfig.url, {
        attribution: tileConfig.attribution
      }).addTo(this.fullscreenMap);

      this.addMarkersToMap(this.fullscreenMap, 'fullscreen');
      this.fitMapToBounds(this.fullscreenMap);
    }
  }

  /**
   * Add markers to a map
   */
  private addMarkersToMap(map: L.Map, mapType: 'minimap' | 'fullscreen'): void {
    if (!map) return;

    this.config.markers.forEach((marker) => {
      const geoPos = marker.geoPosition || this.getStageLocation(marker.stageId);
      // Skip markers without valid positions using our validation helper
      if (!this.isValidPosition(geoPos)) return;

      const stage = this.stages.find(s => s.id === marker.stageId);
      const label = marker.label || stage?.name || marker.stageId;
      const isCurrent = marker.stageId === this.currentStageId;

      // Create custom icon
      const iconHtml = this.createMarkerIconHtml(marker, isCurrent);
      const icon = L.divIcon({
        className: 'custom-map-marker',
        html: iconHtml,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      // Create marker
      const leafletMarker = L.marker([geoPos.lat, geoPos.lng], { icon })
        .addTo(map)
        .bindPopup(label);

      // Click handler
      leafletMarker.on('click', () => {
        this.events.onMarkerClick(marker.stageId);
        if (this.isExpanded) {
          this.setExpanded(false);
        }
      });

      // Hover handlers
      leafletMarker.on('mouseover', () => {
        this.events.onMarkerHover(marker.stageId);
        leafletMarker.openPopup();
      });

      leafletMarker.on('mouseout', () => {
        this.events.onMarkerHover(null);
      });

      // Store marker reference
      const existing = this.markers.get(marker.stageId) || { minimap: null, fullscreen: null };
      existing[mapType] = leafletMarker;
      this.markers.set(marker.stageId, existing);
    });
  }

  /**
   * Create HTML for marker icon
   */
  private createMarkerIconHtml(marker: NavigationMarker, isCurrent: boolean): string {
    const color = marker.color || (isCurrent ? '#FF6B6B' : '#4A90E2');
    const icon = this.getIconSymbol(marker.icon || 'default');

    return `
      <div class="map-marker-pin ${isCurrent ? 'current' : ''}" style="background-color: ${color}">
        <span class="marker-icon-inner">${icon}</span>
        ${isCurrent ? '<div class="marker-pulse-ring"></div>' : ''}
      </div>
    `;
  }

  /**
   * Get icon symbol for marker
   */
  private getIconSymbol(icon: string): string {
    const icons: Record<string, string> = {
      default: '📍',
      star: '★',
      info: 'ℹ',
      camera: '📷',
      video: '🎬',
      audio: '🔊',
    };
    return icons[icon] || icons.default;
  }

  /**
   * Get stage location from stages array
   */
  private getStageLocation(stageId: string): GeoCoordinates | null {
    const stage = this.stages.find(s => s.id === stageId);
    return stage?.location || null;
  }

  /**
   * Check if a position is valid (not null/undefined, not NaN, and not at 0,0)
   */
  private isValidPosition(pos: GeoCoordinates | null | undefined): pos is GeoCoordinates {
    if (pos === null || pos === undefined) return false;
    if (typeof pos.lat !== 'number' || typeof pos.lng !== 'number') return false;
    if (isNaN(pos.lat) || isNaN(pos.lng)) return false;
    // Treat (0, 0) as "not set" since it's in the ocean
    if (pos.lat === 0 && pos.lng === 0) return false;
    return true;
  }

  /**
   * Get default map center
   */
  private getDefaultCenter(): GeoCoordinates {
    if (this.config.map?.defaultCenter) {
      return this.config.map.defaultCenter;
    }

    // Calculate center from markers with valid positions
    const positions = this.config.markers
      .map(m => m.geoPosition || this.getStageLocation(m.stageId))
      .filter((p): p is GeoCoordinates => this.isValidPosition(p));

    if (positions.length === 0) {
      return DEFAULT_MAP_CENTER;
    }

    const avgLat = positions.reduce((sum, p) => sum + p.lat, 0) / positions.length;
    const avgLng = positions.reduce((sum, p) => sum + p.lng, 0) / positions.length;

    return { lat: avgLat, lng: avgLng };
  }

  /**
   * Fit map to marker bounds
   */
  private fitMapToBounds(map: L.Map): void {
    if (!map) return;

    const positions = this.config.markers
      .map(m => m.geoPosition || this.getStageLocation(m.stageId))
      .filter((p): p is GeoCoordinates => this.isValidPosition(p));

    if (positions.length === 0) return;

    if (positions.length === 1) {
      map.setView([positions[0].lat, positions[0].lng], 10);
      return;
    }

    const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }

  /**
   * Center minimap on current stage marker (or first marker if no current stage)
   */
  private centerMinimapOnCurrentStage(): void {
    if (!this.minimapMap) return;

    // Try to find current stage marker, or fall back to first marker
    const targetStageId = this.currentStageId || this.config.markers[0]?.stageId;
    if (!targetStageId) {
      // No markers, fall back to fitting bounds
      this.fitMapToBounds(this.minimapMap);
      return;
    }

    // Get position for target stage
    const marker = this.config.markers.find(m => m.stageId === targetStageId);
    const geoPos = marker?.geoPosition || this.getStageLocation(targetStageId);

    if (this.isValidPosition(geoPos)) {
      // Center on the current/first stage with a focused zoom level
      this.minimapMap.setView([geoPos.lat, geoPos.lng], 12);
    } else {
      // Fall back to fitting all bounds
      this.fitMapToBounds(this.minimapMap);
    }
  }

  /**
   * Set expanded/collapsed state
   */
  public setExpanded(expanded: boolean): void {
    this.isExpanded = expanded;

    if (this.overlayElement) {
      this.overlayElement.setAttribute('data-mode', expanded ? 'fullscreen' : 'minimap');
    }

    if (this.minimapContainer) {
      this.minimapContainer.hidden = expanded && this.config.displayMode === 'fullscreen';
    }

    if (this.fullscreenContainer) {
      this.fullscreenContainer.hidden = !expanded;

      // Invalidate map size when showing fullscreen
      if (expanded && this.fullscreenMap) {
        setTimeout(() => {
          if (this.fullscreenMap) {
            this.fullscreenMap.invalidateSize();
            this.fitMapToBounds(this.fullscreenMap);
          }
        }, 100);
      }
    }

    this.events.onExpandToggle(expanded);
  }

  /**
   * Update the current stage indicator
   */
  public updateCurrentStage(stageId: string): void {
    const previousStageId = this.currentStageId;
    this.currentStageId = stageId;

    // Check if current stage has valid location data
    const currentStageLocation = this.getStageLocation(stageId);
    const marker = this.config.markers.find(m => m.stageId === stageId);
    const markerGeoPos = marker?.geoPosition;
    const hasValidLocation = this.isValidPosition(currentStageLocation) || this.isValidPosition(markerGeoPos);

    // Hide minimap if current stage has no location data
    if (this.minimapContainer) {
      if (hasValidLocation) {
        this.minimapContainer.style.display = '';
        this.minimapContainer.style.opacity = '1';
      } else {
        this.minimapContainer.style.display = 'none';
        logger.debug(`Hiding minimap - stage "${stageId}" has no location data`);
      }
    }

    // Update previous marker
    if (previousStageId) {
      const prevMarkers = this.markers.get(previousStageId);
      if (prevMarkers) {
        this.updateMarkerStyle(prevMarkers, false);
      }
    }

    // Update current marker
    const currentMarkers = this.markers.get(stageId);
    if (currentMarkers) {
      this.updateMarkerStyle(currentMarkers, true);

      // Get coordinates from either minimap or fullscreen marker
      const markerRef = currentMarkers.minimap || currentMarkers.fullscreen;
      if (markerRef) {
        const latlng = markerRef.getLatLng();
        // Validate coordinates before panning
        if (latlng &&
            typeof latlng.lat === 'number' &&
            typeof latlng.lng === 'number' &&
            !isNaN(latlng.lat) &&
            !isNaN(latlng.lng) &&
            (latlng.lat !== 0 || latlng.lng !== 0)) {

          // Center minimap on current marker (higher zoom for focused view)
          if (this.minimapMap) {
            this.minimapMap.setView(latlng, 12, { animate: true, duration: 0.3 });
          }

          // Pan fullscreen map to current marker
          if (this.fullscreenMap) {
            this.fullscreenMap.flyTo(latlng, Math.max(this.fullscreenMap.getZoom(), 8), {
              duration: 0.5
            });
          }
        }
      }
    }
  }

  /**
   * Update marker visual style
   */
  private updateMarkerStyle(markers: { minimap: L.Marker | null; fullscreen: L.Marker | null }, isCurrent: boolean): void {
    const marker = this.config.markers.find(m =>
      m.stageId === (isCurrent ? this.currentStageId : null) ||
      markers.minimap?.options?.stageId === m.stageId
    );

    const iconHtml = this.createMarkerIconHtml(
      marker || { stageId: '', icon: 'default' },
      isCurrent
    );

    const newIcon = L.divIcon({
      className: 'custom-map-marker',
      html: iconHtml,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32]
    });

    if (markers.minimap) {
      markers.minimap.setIcon(newIcon);
    }
    if (markers.fullscreen) {
      markers.fullscreen.setIcon(newIcon);
    }
  }

  /**
   * Check if overlay is currently expanded
   */
  public isCurrentlyExpanded(): boolean {
    return this.isExpanded;
  }

  /**
   * Dispose and clean up
   */
  public dispose(): void {
    if (this.minimapMap) {
      this.minimapMap.remove();
      this.minimapMap = null;
    }
    if (this.fullscreenMap) {
      this.fullscreenMap.remove();
      this.fullscreenMap = null;
    }
    this.markers.clear();
    this.overlayElement?.remove();
    this.overlayElement = null;
    this.minimapContainer = null;
    this.fullscreenContainer = null;
  }
}
